import { apiSlice } from '../../app/services/apiSlice';
import {
  mapApprovalChainFromApi,
  mapApprovalChainListParams,
  mapEligibleApproverFromApi,
} from './approvalChainMappers';
import {
  buildAmendResubmitApiBody,
  buildRecruitmentRequestApiBody,
  mapAssignedRecruitmentRequestStatsFromApi,
  mapMyRecruitmentRequestStatsFromApi,
  mapRecruitmentRequestFromApi,
  mapRecruitmentRequestListParams,
  RECRUITMENT_REQUEST_TIMELINE_RECORD_TYPE,
} from './recruitmentRequestMappers';
import { VIEW_TYPES } from './viewConfig';
import { fetchAuthorityReferenceData } from './service';

/**
 * Approval chains API — see approvalChain.api.md
 * Recruitment requests API — see recruitment-requests API spec.
 * Reference data may still use mock services until its API exists.
 */
export const AUTHORITY_API_ROUTES = {
  recruitmentRequests: 'authority-to-recruit/recruitment-requests',
  recruitmentRequestsAssigned: 'authority-to-recruit/recruitment-requests/assigned',
  statsMyRequests: 'authority-to-recruit/recruitment-requests/stats/my-requests',
  statsAssigned: 'authority-to-recruit/recruitment-requests/stats/assigned',
  recruitmentRequestById: (id) => `authority-to-recruit/recruitment-requests/${id}`,
  recruitmentRequestAction: (id) => `authority-to-recruit/recruitment-requests/${id}/action`,
  recruitmentRequestWithdraw: (id) =>
    `authority-to-recruit/recruitment-requests/${id}/withdraw`,
  recruitmentRequestResubmit: (id) =>
    `authority-to-recruit/recruitment-requests/${id}/resubmit`,
  recruitmentRequestAmendResubmit: (id) =>
    `authority-to-recruit/recruitment-requests/${id}/amend-resubmit`,
  recruitmentRequestComplete: (id) =>
    `authority-to-recruit/recruitment-requests/${id}/complete`,
  recruitmentRequestNotifyApprover: (id) =>
    `authority-to-recruit/recruitment-requests/${id}/notify-approver`,
  eligibleCampaignJobs:
    'authority-to-recruit/recruitment-requests/eligible-campaign-jobs',
  approvalChains: 'authority-to-recruit/approval-chains',
  approvalChainById: (id) => `authority-to-recruit/approval-chains/${id}`,
  approvers: 'authority-to-recruit/approval-chains/approvers',
  referenceData: 'authority-to-recruit/reference-data',
  timeline: 'timeline',
};

const toQueryError = (error) => ({
  error: {
    status: error?.status || 'CUSTOM_ERROR',
    error: error?.data?.message || error?.message || 'Request failed',
    data: {
      message: error?.data?.message || error?.message || 'Request failed',
    },
  },
});

const defaultChainListParams = {
  page: 1,
  perPage: 50,
  sort: 'createdAt',
  sortDir: 'DESC',
};

const AUTHORITY_REQUEST_STATS_TAGS = [
  { type: 'AuthorityRequestStats', id: 'MY_REQUESTS' },
  { type: 'AuthorityRequestStats', id: 'ASSIGNED' },
];

/** Ensures complete API always receives a string recruitment request id in the URL. */
export const resolveRecruitmentRequestId = (arg) => {
  const raw =
    typeof arg === 'string' || typeof arg === 'number'
      ? arg
      : arg?.recruitmentRequestId ?? arg?.id;

  if (raw == null || raw === '') return '';

  if (typeof raw === 'object') {
    const nested = raw.id ?? raw.recruitmentRequestId ?? raw.value;
    return nested != null && nested !== '' ? String(nested) : '';
  }

  return String(raw);
};

/** Normalizes the created job id for the complete request body. */
export const resolveCreatedJobId = (arg) => {
  const raw =
    typeof arg === 'string' || typeof arg === 'number'
      ? arg
      : arg?.jobId ?? arg?.id ?? arg?.job?.id;

  if (raw == null || raw === '') return '';

  if (typeof raw === 'object') {
    const nested = raw.id ?? raw.jobId ?? raw.value;
    return nested != null && nested !== '' ? String(nested) : '';
  }

  return String(raw);
};

/** Normalizes the created campaign id for the complete request body. */
export const resolveCreatedCampaignId = (arg) => {
  const raw =
    typeof arg === 'string' || typeof arg === 'number'
      ? arg
      : arg?.campaignId ?? arg?.campId ?? arg?.id;

  if (raw == null || raw === '') return '';

  if (typeof raw === 'object') {
    const nested = raw.id ?? raw.campaignId ?? raw.campId ?? raw.value;
    return nested != null && nested !== '' ? String(nested) : '';
  }

  return String(raw);
};

const buildCompleteRecruitmentRequestBody = (arg = {}) => {
  const campaignId = resolveCreatedCampaignId(arg?.campaignId ?? arg?.campId);
  if (campaignId) return { campaignId };

  const jobId = resolveCreatedJobId(arg?.jobId);
  if (jobId) return { jobId };

  return {};
};

const buildNotifyApproverBody = ({ accountId, companyId, approverAccountId } = {}) => ({
  accountId,
  companyId,
  ...(approverAccountId ? { approverAccountId } : {}),
});

export const authorityToRecruitApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAuthorityRequests: builder.query({
      query: (params = {}) => {
        const isApproverView = params.viewType === 'approver';
        return {
          url: isApproverView
            ? AUTHORITY_API_ROUTES.recruitmentRequestsAssigned
            : AUTHORITY_API_ROUTES.recruitmentRequests,
          params: mapRecruitmentRequestListParams(params),
        };
      },
      transformResponse: (response) => ({
        requests: (response?.recruitmentRequests || [])
          .map((item) => {
            try {
              return mapRecruitmentRequestFromApi(item);
            } catch {
              return null;
            }
          })
          .filter(Boolean),
        totalCount: response?.totalCount ?? 0,
        perPage: response?.perPage,
        currentPage: response?.currentPage,
        message: response?.message,
      }),
      providesTags: [{ type: 'AuthorityRequest', id: 'LIST' }],
    }),

    getAuthorityRequestStats: builder.query({
      query: (viewType = VIEW_TYPES.APPROVER) => ({
        url:
          viewType === VIEW_TYPES.REQUESTER
            ? AUTHORITY_API_ROUTES.statsMyRequests
            : AUTHORITY_API_ROUTES.statsAssigned,
      }),
      transformResponse: (response, _meta, viewType) =>
        viewType === VIEW_TYPES.REQUESTER
          ? mapMyRecruitmentRequestStatsFromApi(response)
          : mapAssignedRecruitmentRequestStatsFromApi(response),
      providesTags: (_result, _error, viewType) => [
        {
          type: 'AuthorityRequestStats',
          id: viewType === VIEW_TYPES.REQUESTER ? 'MY_REQUESTS' : 'ASSIGNED',
        },
      ],
    }),

    getRecruitmentRequestTimeline: builder.query({
      query: ({ recordId, page = 1, perPage = 50 } = {}) => ({
        url: AUTHORITY_API_ROUTES.timeline,
        params: {
          recordId,
          recordType: RECRUITMENT_REQUEST_TIMELINE_RECORD_TYPE,
          page,
          perPage,
        },
      }),
      transformResponse: (response) => ({
        timelineData: response?.timelineData || [],
        total: response?.total ?? 0,
        page: response?.page,
        perPage: response?.perPage,
        message: response?.message,
      }),
    }),

    getAuthorityRequestById: builder.query({
      query: (arg) => {
        const id = resolveRecruitmentRequestId(arg);
        const oooAccId =
          typeof arg === 'object' && arg?.oooAccId != null ? arg.oooAccId : undefined;

        return {
          url: AUTHORITY_API_ROUTES.recruitmentRequestById(id),
          ...(oooAccId != null && oooAccId !== ''
            ? { params: { oooAccId } }
            : {}),
        };
      },
      transformResponse: (response) => {
        try {
          return {
            request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
            message: response?.message,
          };
        } catch {
          return { request: null, message: response?.message };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'AuthorityRequest', id: resolveRecruitmentRequestId(arg) },
      ],
    }),

    getApprovalChains: builder.query({
      query: (params = {}) => ({
        url: AUTHORITY_API_ROUTES.approvalChains,
        params: mapApprovalChainListParams({
          ...defaultChainListParams,
          ...params,
        }),
      }),
      transformResponse: (response) => ({
        chains: (response?.approvalChains || []).map(mapApprovalChainFromApi),
        totalCount: response?.totalCount ?? 0,
        perPage: response?.perPage,
        currentPage: response?.currentPage,
        message: response?.message,
      }),
      providesTags: [{ type: 'ApprovalChain', id: 'LIST' }],
    }),

    getApprovalChainById: builder.query({
      query: (id) => AUTHORITY_API_ROUTES.approvalChainById(id),
      transformResponse: (response) => ({
        chain: mapApprovalChainFromApi(response?.approvalChain),
        message: response?.message,
      }),
      providesTags: (_result, _error, id) => [{ type: 'ApprovalChain', id }],
    }),

    getAuthorityReferenceData: builder.query({
      async queryFn() {
        try {
          const data = await fetchAuthorityReferenceData();
          return { data };
        } catch (error) {
          return toQueryError(error);
        }
      },
      providesTags: [{ type: 'AuthorityReference', id: 'LIST' }],
    }),

    createApprovalChain: builder.mutation({
      query: (body) => ({
        url: AUTHORITY_API_ROUTES.approvalChains,
        method: 'POST',
        body,
      }),
      transformResponse: (response) => ({
        chain: mapApprovalChainFromApi(response?.approvalChain),
        message: response?.message,
      }),
      invalidatesTags: [{ type: 'ApprovalChain', id: 'LIST' }],
    }),

    updateApprovalChain: builder.mutation({
      query: ({ id, ...body }) => ({
        url: AUTHORITY_API_ROUTES.approvalChainById(id),
        method: 'PUT',
        body,
      }),
      transformResponse: (response) => ({
        chain: mapApprovalChainFromApi(response?.approvalChain),
        message: response?.message,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ApprovalChain', id: 'LIST' },
        { type: 'ApprovalChain', id: arg?.id },
      ],
    }),

    deleteApprovalChain: builder.mutation({
      query: (id) => ({
        url: AUTHORITY_API_ROUTES.approvalChainById(id),
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ApprovalChain', id: 'LIST' }],
    }),

    getAuthorityApprovers: builder.mutation({
      query: () => ({
        url: AUTHORITY_API_ROUTES.approvers,
        method: 'GET',
      }),
      transformResponse: (response) => {
        const options = (response?.chainApprovers || []).map(mapEligibleApproverFromApi);
        return {
          options,
          totalCount: options.length,
          message: response?.message,
        };
      },
    }),

    getEligibleCampaignJobs: builder.mutation({
      query: (params = {}) => ({
        url: AUTHORITY_API_ROUTES.eligibleCampaignJobs,
        method: 'GET',
        params: params.query ? { query: params.query } : undefined,
      }),
      transformResponse: (response) => {
        const jobs = response?.jobs || [];
        return {
          jobs,
          options: jobs.map((job) => ({
            value: job.id,
            label: job.title,
            jobType: job.jobType,
            option: job,
          })),
          totalCount: jobs.length,
          message: response?.message,
        };
      },
    }),

    getAuthorityApprovalChainsListing: builder.mutation({
      query: (params = {}) => ({
        url: AUTHORITY_API_ROUTES.approvalChains,
        method: 'GET',
        params: mapApprovalChainListParams({
          ...defaultChainListParams,
          ...params,
        }),
      }),
      transformResponse: (response) => {
        const chains = (response?.approvalChains || []).map(mapApprovalChainFromApi);
        return {
          approvalChains: chains,
          totalCount: response?.totalCount ?? chains.length,
          options: chains.map((chain) => ({
            value: chain.id,
            label: chain.title || chain.name,
            chain,
          })),
          message: response?.message,
        };
      },
    }),

    createAuthorityRequest: builder.mutation({
      query: (payload) => ({
        url: AUTHORITY_API_ROUTES.recruitmentRequests,
        method: 'POST',
        body: buildRecruitmentRequestApiBody(payload),
      }),
      transformResponse: (response) => {
        try {
          return {
            request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
            message: response?.message,
          };
        } catch {
          return { request: null, message: response?.message };
        }
      },
      invalidatesTags: [
        { type: 'AuthorityRequest', id: 'LIST' },
        ...AUTHORITY_REQUEST_STATS_TAGS,
      ],
    }),

    actionRecruitmentRequest: builder.mutation({
      query: ({ id, action, note, oooAccId }) => ({
        url: AUTHORITY_API_ROUTES.recruitmentRequestAction(id),
        method: 'POST',
        body: {
          action,
          ...(note ? { note } : {}),
          ...(oooAccId != null && oooAccId !== '' ? { oooAccId } : {}),
        },
      }),
      transformResponse: (response) => ({
        request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
        message: response?.message,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AuthorityRequest', id: 'LIST' },
        { type: 'AuthorityRequest', id: arg?.id },
        ...AUTHORITY_REQUEST_STATS_TAGS,
      ],
    }),

    withdrawRecruitmentRequest: builder.mutation({
      query: (id) => ({
        url: AUTHORITY_API_ROUTES.recruitmentRequestWithdraw(id),
        method: 'POST',
      }),
      transformResponse: (response) => ({
        request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
        message: response?.message,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'AuthorityRequest', id: 'LIST' },
        { type: 'AuthorityRequest', id },
        ...AUTHORITY_REQUEST_STATS_TAGS,
      ],
    }),

    resubmitRecruitmentRequest: builder.mutation({
      query: (id) => ({
        url: AUTHORITY_API_ROUTES.recruitmentRequestResubmit(id),
        method: 'POST',
      }),
      transformResponse: (response) => ({
        request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
        message: response?.message,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'AuthorityRequest', id: 'LIST' },
        { type: 'AuthorityRequest', id },
        ...AUTHORITY_REQUEST_STATS_TAGS,
      ],
    }),

    amendResubmitRecruitmentRequest: builder.mutation({
      query: ({ id, body }) => ({
        url: AUTHORITY_API_ROUTES.recruitmentRequestAmendResubmit(id),
        method: 'POST',
        body: buildAmendResubmitApiBody(body),
      }),
      transformResponse: (response) => ({
        request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
        message: response?.message,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AuthorityRequest', id: 'LIST' },
        { type: 'AuthorityRequest', id: arg?.id },
        ...AUTHORITY_REQUEST_STATS_TAGS,
      ],
    }),

    resendRecruitmentRequestNotification: builder.mutation({
      query: (arg) => {
        const id =
          typeof arg === 'string' || typeof arg === 'number' ? arg : arg?.id;
        return {
          url: AUTHORITY_API_ROUTES.recruitmentRequestNotifyApprover(id),
          method: 'POST',
          body: buildNotifyApproverBody(arg),
        };
      },
      transformResponse: (response) => ({
        request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
        message: response?.message,
      }),
      invalidatesTags: (_result, _error, arg) => {
        const id =
          typeof arg === 'string' || typeof arg === 'number' ? arg : arg?.id;
        return [
          { type: 'AuthorityRequest', id: 'LIST' },
          { type: 'AuthorityRequest', id },
        ];
      },
    }),

    completeRecruitmentRequest: builder.mutation({
      query: (arg = {}) => ({
        url: AUTHORITY_API_ROUTES.recruitmentRequestComplete(
          resolveRecruitmentRequestId(arg),
        ),
        method: 'POST',
        body: buildCompleteRecruitmentRequestBody(arg),
      }),
      transformResponse: (response) => ({
        request: mapRecruitmentRequestFromApi(response?.recruitmentRequest),
        message: response?.message,
      }),
      invalidatesTags: (_result, _error, arg) => {
        const recruitmentRequestId = resolveRecruitmentRequestId(arg);
        return [
          { type: 'AuthorityRequest', id: 'LIST' },
          ...(recruitmentRequestId
            ? [{ type: 'AuthorityRequest', id: recruitmentRequestId }]
            : []),
          ...AUTHORITY_REQUEST_STATS_TAGS,
        ];
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAuthorityRequestsQuery,
  useGetAuthorityRequestStatsQuery,
  useGetAuthorityRequestByIdQuery,
  useGetRecruitmentRequestTimelineQuery,
  useGetApprovalChainsQuery,
  useGetApprovalChainByIdQuery,
  useGetAuthorityReferenceDataQuery,
  useCreateApprovalChainMutation,
  useUpdateApprovalChainMutation,
  useDeleteApprovalChainMutation,
  useGetAuthorityApproversMutation,
  useGetEligibleCampaignJobsMutation,
  useGetAuthorityApprovalChainsListingMutation,
  useCreateAuthorityRequestMutation,
  useActionRecruitmentRequestMutation,
  useWithdrawRecruitmentRequestMutation,
  useResubmitRecruitmentRequestMutation,
  useAmendResubmitRecruitmentRequestMutation,
  useCompleteRecruitmentRequestMutation,
  useResendRecruitmentRequestNotificationMutation,
  useLazyGetAuthorityRequestsQuery,
  useLazyGetAuthorityRequestByIdQuery,
  useLazyGetApprovalChainsQuery,
} = authorityToRecruitApiSlice;
